/**
 * Notification service for bedtime story reminders (mobile only).
 * Single source of truth for notification scheduling and scheduled story storage.
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { ALL_THEMES } from '../components/forms/storyConstants';

const BEDTIME_ID = 8000;
const BEDTIME_DAILY_ID = 8001;
const VALID_LANGUAGES = new Set(['en', 'es', 'zh', 'hi', 'ta', 'te', 'mr', 'bn']);
const VALID_SCRIPT_MODES = new Set(['native', 'roman']);

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

export async function requestNotificationPermission() {
  if (!isNativeApp()) return false;
  try {
    const result = await LocalNotifications.requestPermissions();
    return result.display === 'granted';
  } catch {
    return false;
  }
}

export async function checkNotificationPermission() {
  if (!isNativeApp()) return false;
  try {
    const result = await LocalNotifications.checkPermissions();
    return result.display === 'granted';
  } catch {
    return false;
  }
}

export async function scheduleBedtimeNotification({ hour = 20, minute = 0, title, body }) {
  if (!isNativeApp()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: BEDTIME_ID }] });

    // Build explicit future Date to avoid platform-inconsistent behavior
    const now = new Date();
    const target = new Date(now);
    target.setHours(hour, minute, 0, 0);
    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }

    await LocalNotifications.schedule({
      notifications: [{
        id: BEDTIME_ID,
        title: title || 'Story Time! \u{1F4DA}',
        body: body || "Tonight's adventure is waiting!",
        schedule: { at: target },
        extra: { type: 'BEDTIME_STORY' },
      }],
    });
  } catch (err) {
    console.warn('[NOTIFICATIONS] Failed to schedule:', err);
  }
}

export async function cancelBedtimeNotification() {
  if (!isNativeApp()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: BEDTIME_ID }] });
  } catch {}
}

// Daily repeating notification for the Profile bedtime reminder toggle
export async function scheduleDailyBedtimeNotification({ hour = 20, minute = 0 }) {
  if (!isNativeApp()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: BEDTIME_DAILY_ID }] });
    await LocalNotifications.schedule({
      notifications: [{
        id: BEDTIME_DAILY_ID,
        title: 'Story Time! \u{1F4DA}',
        body: "Time for tonight's bedtime story!",
        schedule: { on: { hour, minute }, repeats: true, every: 'day' },
        extra: { type: 'BEDTIME_STORY' },
      }],
    });
  } catch (err) {
    console.warn('[NOTIFICATIONS] Failed to schedule daily:', err);
  }
}

export async function cancelDailyBedtimeNotification() {
  if (!isNativeApp()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: BEDTIME_DAILY_ID }] });
  } catch {}
}

export function setupNotificationListener(navigateCallback) {
  if (!isNativeApp()) return;
  LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    if (action.notification?.extra?.type === 'BEDTIME_STORY') {
      navigateCallback('/create?scheduled=true');
    }
  });
}

// --- Scheduled Story Storage ---

export function getScheduledStory() {
  try {
    const stored = localStorage.getItem('scheduled_story');
    if (!stored) return null;
    const data = JSON.parse(stored);
    const today = new Date().toISOString().split('T')[0];
    if (data.scheduledDate !== today) {
      localStorage.removeItem('scheduled_story');
      return null;
    }
    // Validate theme against known IDs
    if (data.theme !== 'random' && !ALL_THEMES.find(t => t.id === data.theme)) {
      localStorage.removeItem('scheduled_story');
      return null;
    }
    // Validate language/scriptMode
    if (data.language && !VALID_LANGUAGES.has(data.language)) data.language = 'en';
    if (data.scriptMode && !VALID_SCRIPT_MODES.has(data.scriptMode)) data.scriptMode = 'native';
    return data;
  } catch {
    return null;
  }
}

export function saveScheduledStory(data) {
  localStorage.setItem('scheduled_story', JSON.stringify(data));
}

export function clearScheduledStory() {
  localStorage.removeItem('scheduled_story');
}

export function getNotificationPrefs() {
  try {
    return JSON.parse(localStorage.getItem('bedtime_prefs') || '{}');
  } catch {
    return {};
  }
}

export function saveNotificationPrefs(prefs) {
  localStorage.setItem('bedtime_prefs', JSON.stringify(prefs));
}
